import { useEffect, useState } from "react";
import { productBrandService } from "../../../Api/Services/productBrandService";
import { ProductBrand } from "../../../Models/ProductBrand";
import { FaBoxOpen, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import { toast } from "react-toastify";
import SmartNavbar from "../../../Components/SmartNavbar/SmartNavbar";
import FloatingInput from "../../../Components/FloatingInput/FloatingInput";
import BackofficePage from "../../../Components/BackofficePage/BackofficePage";
import Button from "../../../Components/Button/Button";
import BlockedAction from "../../../Components/BlockedAction/BlockedAction";

const DELETE_BLOCKED_REASON = "Esta marca não pode ser excluída porque está vinculada a produtos.";

const AdminProductBrandPage = () => {

  const [brands, setBrands] = useState<ProductBrand[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showBrandForm, setShowBrandForm] = useState<boolean>(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const contentWidthClass = brands.length > 0 ? "max-w-5xl" : "w-full";

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      setIsLoading(true);
      const data = await productBrandService.getAll();
      setBrands(data);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!newName.trim()) {
      return;
    } 

    if (editId) {
      await productBrandService.update({ id: editId, name: newName });
      toast.success("Marca atualizada com sucesso!");
    } else {
      await productBrandService.create({ name: newName });
      toast.success("Marca adicionada com sucesso!");
    }

    setNewName("");
    setEditId(null);
    setShowBrandForm(false);
    load();
  };

  const handleDelete = async (id: string) => {
    try {
      const deleted = await productBrandService.delete(id);

      if (deleted) {
        toast.success("Marca removida com sucesso!");
        load();
      }
    } catch (err: any) {
      toast.error(err.response?.data || "Não foi possível remover a marca, tente novamente mais tarde.");
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SmartNavbar />
      <BackofficePage title={"MARCAS DE PRODUTO"} className={contentWidthClass} fullHeight>

        {showBrandForm ? (
          <div className="mb-4 flex flex-col sm:flex-row gap-2 w-full">
            <FloatingInput
              label={"Nome da marca"}
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
            />
            
            <div className="flex gap-2">
              <Button variant="secondary"
                onClick={() => {
                  setEditId(null);
                  setNewName("");
                  setShowBrandForm(false);
                }}
                className="w-full sm:w-32 px-4 py-2"
              >
                {"CANCELAR"}
              </Button>

              <Button
                onClick={handleSave}
                className="w-full sm:w-32 px-4 py-2"
              >
                {editId ? "SALVAR" : "ADICIONAR"}
              </Button>
            </div>
          </div>
        ) : (
          <button 
            className="uppercase sm:normal-case w-full sm:w-auto flex items-center justify-center sm:justify-start gap-1.5 sm:gap-1 py-2 sm:py-0 rounded sm:rounded-none bg-primary-600 sm:bg-transparent text-white sm:text-primary-600 text-sm sm:text-base font-semibold sm:font-normal my-2 hover:bg-primary-700 sm:hover:bg-transparent sm:hover:text-primary-700"
            onClick={() => setShowBrandForm(true)}
          >
            <FaPlus /> {"Adicionar Marca"}
          </button>
        )}

        {isLoading ? (
          <div className={`mt-6 space-y-4 ${contentWidthClass}`}>
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-white border rounded p-4 shadow-sm">
                <div className="h-4 bg-gray-200 rounded w-1/3 mb-3"></div>
                <div className="h-3 bg-gray-200 rounded w-1/2"></div>
              </div>
            ))}
          </div>
        ) : brands.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-gray-500">
            <FaBoxOpen className="text-3xl" />
            <p className="text-center text-sm sm:text-base">{"Nenhuma marca encontrada"}</p>
          </div>
        ) : (
          <>
            {/* MOBILE CARDS */}
            <div className="flex flex-col gap-4 sm:hidden">
              {brands.map((brand, index) => (
                <div key={brand.id} className={`border rounded p-4 shadow-sm bg-white`}>
                  <h2 className="font-semibold mb-2">
                    {"Marca"} {index + 1}
                  </h2>
                  <p>
                    <span className="font-medium">{"Nome"}:</span> {brand.name}
                  </p>
                  <div className="flex gap-2 mt-3">
                    <Button
                      className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      onClick={() => {
                        setEditId(brand.id);
                        setNewName(brand.name);
                        setShowBrandForm(true);
                      }}
                    >
                      <FaEdit /> {"Editar"}
                    </Button>
                    {brand.canDelete === false ? (
                      <BlockedAction
                        label={"Excluir"}
                        reason={DELETE_BLOCKED_REASON}
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                      >
                        <FaTrash /> {"Excluir"}
                      </BlockedAction>
                    ) : (
                      <Button
                        className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm"
                        onClick={() => handleDelete(brand.id)}
                      >
                        <FaTrash /> {"Excluir"}
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* DESKTOP TABLE */}
            <div className="w-full max-w-5xl">
              <table className={`hidden sm:table w-full border text-left mb-20`}>
                <thead>
                  <tr className="bg-primary-120">
                    <th className="p-2">{"Nome"}</th>
                    <th className="p-2 text-right">{"Ações"}</th>
                  </tr>
                </thead>
                <tbody>
                  {brands.map((cat: any) => (
                    <tr key={cat.id} className="border-t">
                      <td className="p-2">{cat.name}</td>
                      <td className="p-2">
                        <div className="flex justify-end items-center gap-3">
                          <button
                            className="text-primary-600"
                            onClick={() => {
                              setEditId(cat.id);
                              setNewName(cat.name);
                              setShowBrandForm(true);
                            }}
                          >
                            <FaEdit />
                          </button>
                          {cat.canDelete === false ? (
                            <BlockedAction label={"Excluir"} reason={DELETE_BLOCKED_REASON} className="text-gray-300" plain>
                              <FaTrash />
                            </BlockedAction>
                          ) : (
                            <button className="text-primary-600" onClick={() => handleDelete(cat.id)}>
                              <FaTrash />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </BackofficePage>
    </div>
  );
};

export default AdminProductBrandPage;
